import React, { useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Paper,
  InputAdornment,
  Chip,
  LinearProgress,
  Alert,
  Fade,
  IconButton,
  Stack,
  Grid,
  Container,
} from "@mui/material";
import {
  DateRange,
  Description,
  Security,
  Send,
  CloudUpload,
  Close,
} from "@mui/icons-material";
import { baseUrl, baseURL, config, requestID } from "../../../common/utils";
import axios from "axios";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

const WhistlePage = () => {
  const today = new Date().toISOString().split("T")[0];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [formData, setFormData] = useState({
    date: today,
    state_case: "",
    message: "",
    document: null,
    case_id: "",
  });

  const fetchCases = async () => {
    try {
      const payload = { requestID: requestID() };
      const res = await axios.post(
        baseUrl() + "whitsleBlowings/getAllWhistleBlowingCases",
        payload,
        config
      );
      setCases(res.data.data);
    } catch (error) {
      console.error("Failed to fetch cases:", error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      document: files,
    }));
    setSelectedFiles(files);
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setFormData((prev) => ({
      ...prev,
      document: newFiles,
    }));
  };

  const history = useHistory();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    try {
      const payload = new FormData();
      payload.append("requestID", requestID());
      payload.append("date", formData.date);
      payload.append("state_case", selectedCase);
      payload.append("message", formData.message);
      payload.append("case_id", formData.case_id);

      if (formData.document && formData.document.length > 0) {
        formData.document.forEach((file) => {
          payload.append("proof[]", file);
        });
      }

      await axios.post(
        `${baseURL}whitsleBlowings/addWhistleBlowing`,
        payload,
        config
      );

      setSuccess(true);
      setTimeout(() => {
        
        history.push("/folder/view-whistle-blowing/view");
      }, 1000);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  return (
    <IonPage>
      <IonContent>
        <Box
          sx={{
            minHeight: "100vh",
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            py: 3, // Reduced padding
            px: isMobile ? 2 : isTablet ? 3 : 4,
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
            <Box
              sx={{
                maxWidth: "100%",
                mx: "auto",
              }}
            >
              {/* Header Section - Reduced Height */}
              <Paper
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: "white",
                  p: { xs: 2, md: 3 }, // Reduced padding
                  borderRadius: 2, // Smaller border radius
                  mb: 3,
                  textAlign: "center",
                  minHeight: { xs: "120px", md: "140px" }, // Controlled height
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                  <Security sx={{ fontSize: { xs: 32, md: 40 } }} />
                  <Typography
                    variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} // Smaller font sizes
                    fontWeight="bold"
                  >
                    Blow the Whistle
                  </Typography>
                </Box>
                <Typography variant={isMobile ? "body2" : "body1"} sx={{ opacity: 0.9 }}>
                  Report concerns securely and anonymously
                </Typography>
              </Paper>

              {/* Form Section */}
              <Grid container spacing={3}>
                <Grid item xs={12} lg={8} xl={9}>
                  <Card elevation={8} sx={{ borderRadius: 3, overflow: "hidden", height: "100%" }}>
                    <CardContent sx={{ p: { xs: 3, md: 4, lg: 5 } }}>
                      <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        {/* Date and Case Type in Row on larger screens */}
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              disabled
                              label="Submission Date"
                              value={formData.date}
                              fullWidth
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <DateRange color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  backgroundColor: theme.palette.background.default,
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              select
                              fullWidth
                              label="Select Case Type"
                              value={selectedCase}
                              onChange={(e) => {
                                const selectedId = e.target.value;
                                setSelectedCase(selectedId);
                                setFormData((prev) => ({
                                  ...prev,
                                  case_id: selectedId,
                                }));
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Description color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            >
                              {cases?.map((item, index) => (
                                <MenuItem key={index} value={item.case_id}>
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                      {item.name}
                                    </Typography>
                                    {item.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        {item.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>

                        {/* Message Field */}
                        <TextField
                          multiline
                          rows={5} // Reduced rows for more compact view
                          label="Detailed Message"
                          placeholder="Please provide detailed information about your concern..."
                          name="message"
                          value={formData.message}
                          onChange={handleFormChange}
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />

                        {/* File Upload */}
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2, // Reduced padding
                            borderRadius: 2,
                            border: `2px dashed ${theme.palette.primary.main}30`,
                            backgroundColor: theme.palette.background.default,
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: theme.palette.primary.main + "08",
                            },
                          }}
                          onClick={() => document.getElementById("file-input").click()}
                        >
                          <input
                            id="file-input"
                            type="file"
                            multiple
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                          <CloudUpload
                            sx={{ fontSize: 40, color: "primary.main", mb: 1 }} // Smaller icon
                          />
                          <Typography variant="h6" gutterBottom>
                            Upload Supporting Documents
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Click to browse or drag and drop files here
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Maximum file size: 10MB per file
                          </Typography>
                        </Paper>

                        {/* Selected Files */}
                        {selectedFiles.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Selected Files ({selectedFiles.length})
                            </Typography>
                            <Stack spacing={1} direction="row" flexWrap="wrap" gap={1}>
                              {selectedFiles.map((file, index) => (
                                <Chip
                                  key={index}
                                  label={file.name}
                                  onDelete={() => removeFile(index)}
                                  deleteIcon={<Close />}
                                  variant="outlined"
                                  sx={{
                                    maxWidth: "100%",
                                    mb: 1,
                                  }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Loading Indicator */}
                        {loading && (
                          <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
                        )}

                        {/* Submit Button */}
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading || !formData.message || !selectedCase}
                          startIcon={<Send />}
                          sx={{
                            py: 1.5, // Reduced padding
                            borderRadius: 2,
                            fontSize: "1rem", // Smaller font
                            fontWeight: "bold",
                            textTransform: "none",
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: "0 4px 15px 0 rgba(0,0,0,0.2)",
                            "&:hover": {
                              boxShadow: "0 6px 20px 0 rgba(0,0,0,0.3)",
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.3s ease",
                            width: { xs: "100%", md: "auto" },
                            minWidth: 180, // Smaller min-width
                            alignSelf: { xs: "stretch", md: "flex-start" },
                          }}
                        >
                          {loading ? "Submitting Report..." : "Submit Report"}
                        </Button>

                        {/* Success Message */}
                        <Fade in={success}>
                          <Alert
                            severity="success"
                            sx={{
                              borderRadius: 2,
                              "& .MuiAlert-message": {
                                width: "100%",
                                textAlign: "center",
                              },
                            }}
                          >
                            <Typography variant="h6" gutterBottom>
                              Report Submitted Successfully!
                            </Typography>
                            <Typography variant="body2">
                              Your whistleblower report has been received securely.
                              Redirecting...
                            </Typography>
                          </Alert>
                        </Fade>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Sidebar/Info Panel for larger screens */}
                <Grid item xs={12} lg={4} xl={3}>
                  <Card elevation={8} sx={{ borderRadius: 3, overflow: "hidden", height: "100%" }}>
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                        📋 Reporting Guidelines
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Be Specific
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Provide clear dates, times, locations, and individuals involved.
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Include Evidence
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Attach relevant documents, emails, or photos that support your claim.
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Stay Anonymous
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Your identity will be protected throughout the process.
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Follow Up
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Check your dashboard for updates on your report status.
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Footer Info */}
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                sx={{ display: "block", mt: 3 }}
              >
                All reports are handled with strict confidentiality and security
              </Typography>
            </Box>
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default WhistlePage;